import { useEffect, useMemo, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";

/**
 * Animated counter that parses a stat value like "$142M+", "180+", "3.8x", "94%"
 * and animates the numeric portion from 0 to target when scrolled into view.
 */
export default function CountUp({ value, duration = 1.8, className = "", accentClassName = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const parsed = useMemo(() => {
    const m = value.match(/^(\$)?(\d+(?:\.\d+)?)([MKB])?([+%x])?$/i);
    if (!m) return null;
    return {
      prefix: m[1] || "",
      target: parseFloat(m[2]),
      unit: m[3] || "",
      suffix: m[4] || "",
      isDecimal: m[2].includes("."),
    };
  }, [value]);

  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView || !parsed) return;
    const controls = animate(0, parsed.target, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, parsed, duration]);

  if (!parsed) {
    return <span className={className}>{value}</span>;
  }

  const formatted = parsed.isDecimal ? display.toFixed(1) : Math.round(display).toLocaleString();

  return (
    <span ref={ref} className={className}>
      {parsed.prefix}
      {formatted}
      {parsed.unit}
      {parsed.suffix && <span className={accentClassName}>{parsed.suffix}</span>}
    </span>
  );
}
